<script setup lang="ts">
interface Props {
  title: string;
  subtitle?: string;
  content?: string | boolean;
  list?: string[];
  initialOpen?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  subtitle: "",
  content: false,
  list: () => [],
  initialOpen: false,
});

const isOpen = ref(props.initialOpen);

function toggle() {
  isOpen.value = !isOpen.value;
}
</script>

<template>
  <div class="accordion">
    <div
      class="accordion__header"
      @click="toggle"
    >
      <span class="accordion__title">{{ title }}</span>
      <fa
        :icon="isOpen ? ['fal', 'chevron-up'] : ['fal', 'chevron-down']"
        class="accordion__chevron"
      />
    </div>
    <Transition name="accordion">
      <div
        v-if="isOpen"
        class="accordion__content"
      >
        <slot>
          <p
            v-if="subtitle"
            class="accordion__subtitle small-text light-grey-text"
          >
            {{ subtitle }}
          </p>
          <p
            v-if="content"
            class="accordion__subtitle small-text light-grey-text"
            v-html="content"
          />
          <ul v-if="list && list.length">
            <li
              v-for="(item, i) in list"
              :key="i"
            >
              {{ item.charAt(0).toUpperCase() + item.slice(1) }}
            </li>
          </ul>
        </slot>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss">
.accordion {
  width: 100%;
  border-bottom: 2px solid var(--color-grey-3);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: calc(100% - 1rem);
    cursor: pointer;
    background: none;
    border: none;
    padding: 0.5rem 0;
    font: inherit;
    text-align: left;
  }

  &__title {
    font-size: 1rem;
    color: var(--color-grey-1);
  }

  &__chevron {
    color: var(--color-pink-1);
    cursor: pointer;
  }

  &__content {
    padding-bottom: 1rem;
    font-family: "GalanoGrotesqueMedium", sans-serif;

    ul {
      list-style: disc;
      padding-left: 1.25rem;
    }
  }

  &__subtitle {
    font-size: 0.85rem;
    color: var(--color-grey-2);
    margin-bottom: 0.5rem;
  }
}

/* Transition */
.accordion-enter-active,
.accordion-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.accordion-enter-from,
.accordion-leave-to {
  opacity: 0;
  max-height: 0;
}

.accordion-enter-to,
.accordion-leave-from {
  opacity: 1;
  max-height: 500px;
}
</style>
